import { apiClient, ApiResponse } from './api'
import { LoginRequest, LoginResponse, AuthUser } from '../types'

export class AuthApi {
  private static instance: AuthApi

  public static getInstance(): AuthApi {
    if (!AuthApi.instance) {
      AuthApi.instance = new AuthApi()
    }
    return AuthApi.instance
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await apiClient.post<LoginResponse>(
        '/auth-service/api/v1/users/login',
        credentials,
        undefined,
        'AUTH'
      )
      
      if (response.success && (response.data as any)?.user) {
        const userData = (response.data as any).user
        
        // Store tokens in localStorage
        localStorage.setItem('accessToken', userData.accessToken)
        localStorage.setItem('refreshToken', userData.refreshToken)
        localStorage.setItem('user', JSON.stringify(userData))
        localStorage.setItem('isAuthenticated', 'true')
      }
      
      return response
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  /**
   * Logout user and clear stored data
   */
  async logout(): Promise<void> {
    try {
      // Call logout API endpoint to invalidate token on server
      await apiClient.post(
        '/auth-service/api/v1/auth/logout',
        {},
        undefined,
        'AUTH'
      )
    } catch (error) {
      console.error('Logout API error:', error)
      // Continue to clear local storage even if API call fails
    } finally {
      // Clear stored data regardless of API call success
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('selectedRole')
      localStorage.removeItem('tenantId')
    }
  }

  /**
   * Get current user from localStorage
   */
  getCurrentUser(): AuthUser | null {
    try {
      const userStr = localStorage.getItem('user')
      
      if (userStr) {
        return JSON.parse(userStr) as AuthUser
      }
      return null
    } catch (error) {
      console.error('Error getting current user:', error)
      return null
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('accessToken')
    const isAuth = localStorage.getItem('isAuthenticated')
    return !!(token && isAuth === 'true')
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<ApiResponse<{ accessToken: string }>> {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await apiClient.post<{ accessToken: string }>(
        '/auth-service/api/v1/auth/refresh',
        { refreshToken },
        undefined,
        'AUTH'
      )
      
      if (response.success && response.data?.accessToken) {
        // Update stored access token
        localStorage.setItem('accessToken', response.data.accessToken)
        console.log('Access token refreshed successfully')
      }
      
      return response
    } catch (error) {
      console.error('Token refresh error:', error)
      // If refresh fails, logout user
      await this.logout()
      throw error
    }
  }

  /**
   * Signup user
   */
  async signup(signupData: {
    email: string
    password: string
    confirmPassword: string
  }): Promise<ApiResponse<LoginResponse>> {
    try {
      const response = await apiClient.post<LoginResponse>(
        '/auth-service/api/v1/users/signup',
        signupData,
        undefined,
        'AUTH'
      )
      
      if (response.success && (response.data as any)?.user) {
        const userData = (response.data as any).user
        
        // Store tokens in localStorage
        localStorage.setItem('accessToken', userData.accessToken)
        localStorage.setItem('refreshToken', userData.refreshToken)
        localStorage.setItem('user', JSON.stringify(userData))
        localStorage.setItem('isAuthenticated', 'true')
        
        // Store additional data
        localStorage.setItem('selectedRole', 'client') // New users start as clients
      }
      
      return response
    } catch (error) {
      console.error('Signup error:', error)
      throw error
    }
  }

  /**
   * Validate current token
   */
  async validateToken(): Promise<boolean> {
    try {
      const token = localStorage.getItem('accessToken')
      if (!token) {
        return false
      }

      // You can add a token validation endpoint here
      // For now, we'll just check if token exists and is not expired
      return true
    } catch (error) {
      console.error('Token validation error:', error)
      return false
    }
  }

  /**
   * Get user profile
   */
  async getProfile(): Promise<ApiResponse<AuthUser>> {
    try {
      const response = await apiClient.get<AuthUser>(
        '/auth-service/api/v1/users/profile',
        undefined,
        'AUTH'
      )
      return response
    } catch (error) {
      console.error('Get profile error:', error)
      throw error
    }
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: {
    firstName: string
    lastName: string
    phone: string
  }): Promise<ApiResponse<AuthUser>> {
    try {
      const response = await apiClient.patch<AuthUser>(
        '/auth-service/api/v1/users/profile',
        profileData,
        undefined,
        'AUTH'
      )
      
      if (response.success && response.data) {
        // Update stored user data
        const currentUser = this.getCurrentUser()
        if (currentUser) {
          const updatedUser = { ...currentUser, ...profileData }
          localStorage.setItem('user', JSON.stringify(updatedUser))
        }
      }
      
      return response
    } catch (error) {
      console.error('Update profile error:', error)
      throw error
    }
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: {
    oldPassword: string
    password: string
    confirmPassword: string
  }): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.patch<{ message: string }>(
        '/auth-service/api/v1/users/change-password',
        passwordData,
        undefined,
        'AUTH'
      )
      return response
    } catch (error) {
      console.error('Change password error:', error)
      throw error
    }
  }

  /**
   * Request password reset - sends reset link to user's email
   */
  async requestPasswordReset(email: string): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.post<{ message: string }>(
        '/auth-service/api/v1/users/request-password-reset',
        { email },
        undefined,
        'AUTH'
      )
      return response
    } catch (error) {
      console.error('Request password reset error:', error)
      throw error
    }
  }

  /**
   * Validate password reset token
   */
  async validateResetToken(token: string): Promise<ApiResponse<{ valid: boolean; message?: string }>> {
    try {
      const response = await apiClient.post<{ valid: boolean; message?: string }>(
        '/auth-service/api/v1/users/validate-reset-password-token',
        { token },
        undefined,
        'AUTH'
      )
      return response
    } catch (error) {
      console.error('Validate reset token error:', error)
      throw error
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(resetData: {
    token: string
    password: string
    confirmPassword: string
  }): Promise<ApiResponse<{ message: string }>> {
    try {
      const response = await apiClient.post<{ message: string }>(
        '/auth-service/api/v1/users/reset-password',
        resetData,
        undefined,
        'AUTH'
      )
      return response
    } catch (error) {
      console.error('Reset password error:', error)
      throw error
    }
  }
}

export const authApi = AuthApi.getInstance()
export default authApi
